/**
 * Coverage — SPEC §9.3, the planning view.
 *
 * A date × location grid answering "what can I create in Enrollware, and where?". Every
 * ACTIVE location gets a row whether or not anybody covers it, because a site nobody covers
 * dropping out of the grid is the exact failure §4.6 exists to catch.
 *
 * ## Color is never the only signal
 *
 * Each cell carries its count as a number and a title, and the exposure list restates the
 * same facts in words. A red/amber/green grid that only means something in color is
 * unreadable to about one man in twelve (SPEC §12.2).
 *
 * ## Unconfirmed is shown, but never counted as cover
 *
 * A day past an instructor's horizon appears as a small "+n" and keeps the cell at its
 * confirmed level. Folding it in would let a class be built on a day nobody committed to
 * (SPEC §3.3.2).
 */

import { useEffect, useMemo, useState } from 'react';
import { dayOfWeekIndex, parseISO } from '@gearup/shared';
import { adminApi, type CoverageCell, type CoverageGrid, type Period } from '../../lib/api.js';

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

const PERIODS: { value: Period | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All day parts' },
  { value: 'AM', label: 'Mornings' },
  { value: 'PM', label: 'Afternoons' },
  { value: 'ALL_DAY', label: 'No set time' },
];

export function Coverage({ cycleKey }: { cycleKey: string }) {
  const [grid, setGrid] = useState<CoverageGrid | null>(null);
  const [period, setPeriod] = useState<Period | 'ALL'>('ALL');
  const [selected, setSelected] = useState<CoverageCell | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setSelected(null);
    void adminApi
      .coverage({ cycle: cycleKey, ...(period === 'ALL' ? {} : { period }) })
      .then((data) => {
        if (!canceled) setGrid(data);
      })
      .catch(() => {
        if (!canceled) setError('Could not load coverage.');
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [cycleKey, period]);

  const unstaffed = useMemo(
    () => grid?.summaries.filter((s) => s.instructorCount === 0) ?? [],
    [grid],
  );

  if (loading && !grid) return <p className="muted center">Loading coverage…</p>;
  if (error) return <p className="notice notice--error">{error}</p>;
  if (!grid) return null;

  return (
    <div className="stack">
      <div className="card stack">
        <div className="row row--between row--wrap">
          <h1 style={{ margin: 0 }}>{grid.label} coverage</h1>
          <label className="field" style={{ minWidth: 180 }}>
            <span className="field__label">Show</span>
            <select
              className="select"
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period | 'ALL')}
            >
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="muted" style={{ margin: 0 }}>
          <strong>{grid.totals.confirmed}</strong> confirmed
          {grid.totals.unconfirmed > 0 && <> · {grid.totals.unconfirmed} not yet confirmed</>} across{' '}
          {grid.locations.length} location{grid.locations.length === 1 ? '' : 's'}.
        </p>

        {/* Sites with nobody on them at all are a different, worse problem than a month
            that has not come in yet, so they are called out above the grid rather than
            being one more red row inside it (SPEC §4.6). */}
        {unstaffed.length > 0 && (
          <div className="notice notice--error" style={{ alignItems: 'flex-start' }}>
            <span className="notice__icon" aria-hidden="true">!</span>
            <span>
              <strong>
                {unstaffed.length} location{unstaffed.length === 1 ? ' has' : 's have'} no active
                instructor assigned
              </strong>
              : {unstaffed.map((s) => s.name).join(', ')}.
              <br />
              <span className="small">
                No month will ever fill these in. They need somebody assigned, or deactivating.
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="card stack">
        <div className="coverage__scroll">
          <table className="coverage">
            <thead>
              <tr>
                <th scope="col" className="coverage__corner">
                  Location
                </th>
                {grid.dates.map((date) => {
                  const d = parseISO(date);
                  return (
                    <th key={date} scope="col" className="coverage__date">
                      <span aria-hidden="true">{WEEKDAY_INITIALS[dayOfWeekIndex(d)]}</span>
                      <span className="coverage__daynum">{d.day}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grid.locations.map((location) => (
                <tr key={location.id}>
                  <th scope="row" className="coverage__name">
                    {location.name}
                    {(grid.summaries.find((s) => s.locationId === location.id)?.instructorCount ??
                      0) === 0 && (
                      <span className="coverage__flag" title="No active instructor assigned">
                        nobody assigned
                      </span>
                    )}
                  </th>
                  {grid.dates.map((date) => {
                    const cell = grid.cells[`${date}|${location.id}`];
                    const confirmed = cell?.confirmed ?? 0;
                    const unconfirmed = cell?.unconfirmed ?? 0;
                    const level = cell?.level ?? 'gap';
                    const label =
                      `${location.name}, ${date}: ${confirmed} confirmed` +
                      (unconfirmed ? `, ${unconfirmed} not yet confirmed` : '');
                    return (
                      <td key={date} className="coverage__cellwrap">
                        <button
                          type="button"
                          className={`coverage__cell coverage__cell--${level}`}
                          title={label}
                          aria-label={label}
                          disabled={!cell}
                          onClick={() => cell && setSelected(cell)}
                        >
                          {/* The number, not just the color. */}
                          <span>{confirmed}</span>
                          {unconfirmed > 0 && (
                            <span className="coverage__pending">+{unconfirmed}</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="legend">
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--gap" aria-hidden="true" />
            <span>0 — nothing can run</span>
          </span>
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--single" aria-hidden="true" />
            <span>1 — one withdrawal kills it</span>
          </span>
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--covered" aria-hidden="true" />
            <span>2+ — covered</span>
          </span>
          <span className="legend__item">
            <span className="legend__badge" aria-hidden="true">+n</span>
            <span>marked, not yet confirmed — never counted as cover</span>
          </span>
        </div>
      </div>

      {selected && <CellDetail cell={selected} grid={grid} onClose={() => setSelected(null)} />}
    </div>
  );
}

function CellDetail({
  cell,
  grid,
  onClose,
}: {
  cell: CoverageCell;
  grid: CoverageGrid;
  onClose: () => void;
}) {
  const location = grid.locations.find((l) => l.id === cell.locationId);
  const date = parseISO(cell.date);

  return (
    <div className="card stack">
      <div className="row row--between">
        <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>
          {location?.name} · {date.month}/{date.day}
        </h2>
        <button type="button" className="daylist__remove" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      {cell.instructors.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>Nobody is available.</p>
      ) : (
        <ul className="daylist">
          {cell.instructors.map((i) => (
            <li
              key={`${i.instructorId}-${i.period}-${i.startTime ?? ''}`}
              className={`daylist__row${i.provisional ? ' daylist__row--provisional' : ''}`}
            >
              <span className="daylist__date">
                {i.name}
                <br />
                <span className="daylist__meta">
                  {i.startTime ?? (i.period === 'ALL_DAY' ? 'No set time' : i.period)}
                  {i.provisional && ' · not confirmed yet'}
                  {i.note && ` · “${i.note}”`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
